import { Alert, Box, CircularProgress, IconButton, LinearProgress, Paper, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import GetAppIcon from "@mui/icons-material/GetApp";
import { useDropzone } from "react-dropzone";
import Button from "../../../../shared/OAWorkflow/components/atoms/Button";
import { XLSX_CONTENT_TYPE } from "../../utils/downloadHelpers";
import { MAX_FILE_BYTES, MAX_FILE_MB, dropRejectionMessage, formatFileSize } from "../constants";

/**
 * File chooser for the inventor bulk upload: template download, dropzone, the selected
 * file. Stateless - the caller owns the file and the request.
 *
 * Deliberately renders no submit button: the import starts as soon as a file is chosen, so
 * the dialog's action row holds only Cancel, which becomes Done once a summary is on
 * screen.
 */
const UploadPanel = ({
  file,
  onFileSelected,
  onFileRejected,
  onClearFile,
  onDownloadTemplate,
  uploading,
  downloadingTemplate,
  error,
}) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, fileRejections) => {
      if (acceptedFiles.length > 0) {
        onFileSelected(acceptedFiles[0]);
        return;
      }
      // A rejection hands back an empty accepted list, so without this the drop looks like
      // nothing happened at all.
      if (fileRejections.length > 0) onFileRejected(dropRejectionMessage(fileRejections));
    },
    // react-dropzone v14 takes a MIME-type map, not the v11 extension string: given a bare
    // ".xlsx" it filters nothing and warns on every render.
    accept: { [XLSX_CONTENT_TYPE]: [".xlsx"] },
    // `multiple: false` rather than `maxFiles: 1`, which leaves the input multi-select and
    // then rejects the whole selection.
    multiple: false,
    // Turns an oversized file away before the browser reads it, rather than after -
    // `validateFile` still checks, for the paths that never touch the dropzone.
    maxSize: MAX_FILE_BYTES,
    disabled: uploading,
  });

  return (
    <Box data-testid="inventor-bulk-upload-panel">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5" sx={{ color: "#1E293B", fontSize: "20px", fontWeight: 400 }}>
          Upload File
        </Typography>
        <Button
          variant="secondary"
          startIcon={downloadingTemplate ? <CircularProgress size={16} color="inherit" /> : <GetAppIcon />}
          onClick={onDownloadTemplate}
          disabled={downloadingTemplate}
          sx={{ border: "none" }}
        >
          Download Template
        </Button>
      </Box>

      <Paper
        elevation={0}
        sx={{ p: 4, border: "1px solid #E0E0E0", borderRadius: 2, backgroundColor: "#fff" }}
      >
        <Box
          {...getRootProps()}
          data-testid="inventor-bulk-upload-dropzone"
          sx={{
            p: 4,
            border: "1px dashed #E0E0E0",
            borderRadius: 2,
            textAlign: "center",
            cursor: uploading ? "not-allowed" : "pointer",
            mb: file ? 3 : 0,
            transition: "all 0.2s",
            backgroundColor: isDragActive ? "#F2F7FC" : "transparent",
            "&:hover": { borderColor: "#005CB9", backgroundColor: "#F2F7FC" },
          }}
        >
          <input {...getInputProps()} />
          <DescriptionIcon sx={{ fontSize: 32, color: "#005CB9", mb: 1.5, opacity: 0.8 }} />
          <Typography variant="body1" sx={{ color: "#64748B", fontWeight: 400 }}>
            {isDragActive ? (
              "Drop the file here"
            ) : (
              <>
                <span style={{ color: "#005CB9", textDecoration: "underline", fontWeight: 500 }}>Click to upload</span>{" "}
                or drag and drop
              </>
            )}
          </Typography>
          <Typography variant="body2" sx={{ color: "#94A3B8", mt: 0.5 }}>
            XLSX only (max. {MAX_FILE_MB}MB)
          </Typography>
        </Box>

        {file && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1.5,
              px: 2,
              border: "1px solid #F0F0F0",
              borderRadius: 1.5,
              backgroundColor: "#FAFAFA",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <DescriptionIcon sx={{ color: "#005CB9" }} />
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(file.size)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                size="small"
                aria-label="Remove selected file"
                disabled={uploading}
                onClick={(event) => {
                  event.stopPropagation();
                  onClearFile();
                }}
              >
                <DeleteIcon sx={{ fontSize: 20, color: "#999" }} />
              </IconButton>
              <CheckCircleIcon sx={{ color: "#4CAF50", fontSize: 22 }} />
            </Box>
          </Box>
        )}

        {uploading && <LinearProgress sx={{ mt: 3, borderRadius: 1 }} />}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mt: 2, borderRadius: 1.5 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default UploadPanel;
